import { createSlice } from '@reduxjs/toolkit';
import { LayoutUtils } from '@miroculus/nucleo';
import { addLog } from '../log';
// How much time the electrode with a droplet is red, default: one minute
const DROPLET_DETECTION_TIME = Number(process.env.DROPLET_DETECTION_TIME) || 60000;
// If droplet detections are performed in less than 1.5 seconds the detections will be accumulated
// to maintain the droplets representation active in the electrolayout UI
const MAX_ACCUMULATION_TIME = 1500;
const { loadLayout, getPolygonBySoftwareId } = LayoutUtils;
const MULTIELECTRODES_ELECTROPADS = ['amalthea'];
const initialState = {
    layout: { polygons: [] },
    moveBackbone: [],
    moveBackboneTimestamp: 0,
};
const electrodeLayoutSlice = createSlice({
    name: 'electrodeLayout',
    initialState,
    reducers: {
        loadElectrodeLayout: (state, action) => {
            state.layout = loadLayout(action.payload);
        },
        updateElectrodeLayout: (state, action) => {
            state.layout = { ...state.layout, ...action.payload };
        },
        turnOnElectrode: {
            reducer: (state, action) => {
                const { electrodeId, timestamp } = action.payload;
                const polygonIndex = getPolygonBySoftwareId(state.layout, electrodeId);
                const polygon = state.layout.polygons[polygonIndex];
                if (!polygon)
                    return;
                const layoutName = (state.layout.name || '').toLowerCase();
                const multielectrode = MULTIELECTRODES_ELECTROPADS.find((e) => layoutName === e);
                if (multielectrode) {
                    state.layout.polygons = state.layout.polygons.map((poly) => {
                        if (polygon.label !== poly.label)
                            return poly;
                        if (timestamp < (poly.timestamp || 0))
                            return poly;
                        return { ...poly, status: true, timestamp };
                    });
                }
                else {
                    if (!polygon.timestamp || timestamp > polygon.timestamp) {
                        state.layout.polygons[polygonIndex] = {
                            ...polygon,
                            status: true,
                            timestamp,
                        };
                    }
                }
            },
            prepare: (electrodeId, timestamp) => ({
                payload: { electrodeId, timestamp },
            }),
        },
        turnOffElectrode: {
            reducer: (state, action) => {
                const { electrodeId, timestamp } = action.payload;
                const polygonIndex = getPolygonBySoftwareId(state.layout, electrodeId);
                const polygon = state.layout.polygons[polygonIndex];
                const layoutName = (state.layout.name || '').toLowerCase();
                const multielectrode = MULTIELECTRODES_ELECTROPADS.find((e) => layoutName === e);
                if (!polygon)
                    return;
                if (multielectrode) {
                    state.layout.polygons = state.layout.polygons.map((poly) => {
                        if (polygon.label !== poly.label)
                            return poly;
                        if (timestamp < (poly.timestamp || 0))
                            return poly;
                        return { ...poly, status: false, timestamp };
                    });
                }
                else {
                    if (!polygon.timestamp || timestamp > polygon.timestamp) {
                        state.layout.polygons[polygonIndex] = {
                            ...polygon,
                            status: false,
                            timestamp,
                        };
                    }
                }
            },
            prepare: (electrodeId, timestamp) => ({
                payload: { electrodeId, timestamp },
            }),
        },
        turnOnElectrodes: {
            reducer: (state, action) => {
                const { electrodeIds, timestamp } = action.payload;
                state.layout.polygons = state.layout.polygons.map((poly) => {
                    if (!electrodeIds.includes(poly.label))
                        return poly;
                    if (poly.timestamp && timestamp < poly.timestamp)
                        return poly;
                    return { ...poly, status: true, timestamp };
                });
            },
            prepare: (values, timestamp) => ({
                payload: { electrodeIds: values, timestamp },
            }),
        },
        turnOffElectrodes: {
            reducer: (state, action) => {
                const { electrodeIds, timestamp } = action.payload;
                state.layout.polygons = state.layout.polygons.map((poly) => {
                    if (!electrodeIds.includes(poly.label))
                        return poly;
                    if (poly.timestamp && timestamp < poly.timestamp)
                        return poly;
                    return { ...poly, status: false, timestamp };
                });
            },
            prepare: (values, timestamp) => ({
                payload: { electrodeIds: values, timestamp },
            }),
        },
        turnOffAllElectrodes: {
            reducer: (state, action) => {
                const { timestamp } = action.payload;
                state.layout.polygons = state.layout.polygons.map((poly) => {
                    if (poly.timestamp && timestamp < poly.timestamp)
                        return poly;
                    return { ...poly, status: false, timestamp };
                });
            },
            prepare: (timestamp) => ({
                payload: { timestamp },
            }),
        },
        setAllDroplets: (state, action) => {
            const values = action.payload;
            state.layout.polygons = state.layout.polygons.map((poly) => ({
                ...poly,
                content: values[poly.label] || 0,
            }));
        },
        setSingleDroplet: {
            reducer: (state, action) => {
                const { electrodeId, hasDroplet } = action.payload;
                const polygonIndex = getPolygonBySoftwareId(state.layout, electrodeId);
                const polygon = state.layout.polygons[polygonIndex];
                state.layout.polygons[polygonIndex] = {
                    ...polygon,
                    content: hasDroplet,
                };
            },
            prepare: (electrodeId, hasDroplet) => ({
                payload: { electrodeId, hasDroplet },
            }),
        },
        updateMoveBackbone: {
            reducer: (state, action) => {
                const { moveBackbone, timestamp } = action.payload;
                state.layout.polygons = state.layout.polygons.map((poly) => ({
                    ...poly,
                    backbone: moveBackbone.includes(Number(poly.label)),
                }));
                state.moveBackbone = moveBackbone;
                state.moveBackboneTimestamp = timestamp;
            },
            prepare: (moveBackbone, timestamp) => ({
                payload: { moveBackbone, timestamp: timestamp || 0 },
            }),
        },
    },
});
// Export actions
export const { loadElectrodeLayout, updateElectrodeLayout, turnOnElectrode, turnOffElectrode, turnOnElectrodes, turnOffElectrodes, turnOffAllElectrodes, setAllDroplets, setSingleDroplet, updateMoveBackbone, } = electrodeLayoutSlice.actions;
// Thunk Actions (kept for cross-module dispatches and side effects)
const getDetectionsAsArray = (values) => Object.entries(values)
    .filter(([, v]) => v > 0)
    .map(([k]) => k);
let addingDetectionsTimeout;
let addingDetections = false;
export const setDroplets = (values) => (dispatch, getState) => {
    if (addingDetections) {
        const { polygons } = getState().electrodeLayout.layout;
        const oldValues = polygons
            .filter((p) => p.content > 0)
            .reduce((acc, p) => ({
            ...acc,
            [p.label]: p.content,
        }), {});
        dispatch(setAllDroplets({ ...values, ...oldValues }));
        clearTimeout(addingDetectionsTimeout);
    }
    else {
        addingDetections = true;
        dispatch(setAllDroplets(values));
    }
    addingDetectionsTimeout = setTimeout(() => {
        addingDetections = false;
    }, MAX_ACCUMULATION_TIME);
    const detectedDroplets = getDetectionsAsArray(values);
    if (detectedDroplets.length) {
        dispatch(addLog(`[DROPLETS DETECTED] ${JSON.stringify(detectedDroplets)}`, 'out', 'droplets'));
        setTimeout(() => {
            // clear electrodes after DROPLET_DETECTION_TIME
            const clearValues = {};
            Object.keys(values).forEach((key) => {
                clearValues[key] = 0;
            });
            dispatch(setAllDroplets(clearValues));
        }, DROPLET_DETECTION_TIME);
    }
};
export const setDroplet = (electrodeId, answer) => (dispatch) => {
    const hasDroplet = answer.length > 0 && answer[0] !== 0;
    dispatch(setSingleDroplet(electrodeId, hasDroplet));
    if (hasDroplet) {
        dispatch(addLog(`[DROPLET DETECTED] ${electrodeId}`, 'out', 'droplets'));
        // clear electrode after DROPLET_DETECTION_TIME
        setTimeout(() => {
            dispatch(setSingleDroplet(electrodeId, false));
        }, DROPLET_DETECTION_TIME);
    }
};
// Export reducer as default
export default electrodeLayoutSlice.reducer;
